import { z } from 'zod'

import type {
  CreateReportCommentData,
  CreateReportData,
  GetEssayScoringsData,
  GetEssayScoringSummariesByGradeData,
  GetEssayScoringSummariesByLessonClassData,
  GetGroupReportData,
  UpdateEssayScoringData,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  type CreatedEssayReport,
  CreatedEssayReportSchema,
  type EssayReportComment,
  EssayReportCommentSchema,
  type EssayReportFile,
  EssayReportFileSchema,
  type EssayScoringDetail,
  EssayScoringDetailSchema,
  type EssayScoringSaveResult,
  EssayScoringSaveResultSchema,
  type EssayScoringStudent,
  EssayScoringStudentSchema,
} from './schemas'

const ENTITY = 'EssayScoring'

// 서버의 Kotlin nullable 응답 필드를 스펙이 optional 로만 그린다(nullable 과소표기). 실제로는 키가 오고 값이 null 이다.
//   학생: score·scoringOpenDatetime / 문항 채점: score·feedback·solutionHandwritingUrl, 슬롯의 handwritingUrl·
//   result·recognizedAnswer / 저장 결과: score / 유형별 집계: comment / 보고서 생성: creverseShare
// 2026-10-07 백엔드 origin/develop(85d9b076a) 의 essayScoring/controller/response 로 확인. 전역 NON_NULL 설정이 없다.
// 스키마가 실제 모양을 검증하므로 입력은 생성 타입으로 받되 이 한 줄에서만 unknown 을 거친다.
const asWire = (
  raw:
    | GetEssayScoringSummariesByGradeData
    | GetEssayScoringSummariesByLessonClassData
    | GetEssayScoringsData
    | UpdateEssayScoringData
    | CreateReportCommentData
    | CreateReportData,
): unknown => raw

// 서술형 채점 엔드포인트는 공통 봉투({ data, code, message }) 없이 본문을 그대로 보낸다
export const adaptEssayScoringStudents = (
  raw: GetEssayScoringSummariesByGradeData | GetEssayScoringSummariesByLessonClassData,
): EssayScoringStudent[] =>
  parseApiResponse(z.array(EssayScoringStudentSchema), asWire(raw), ENTITY)

export const adaptEssayScoringDetail = (raw: GetEssayScoringsData): EssayScoringDetail =>
  parseApiResponse(EssayScoringDetailSchema, asWire(raw), ENTITY)

export const adaptEssayScoringSaveResult = (raw: UpdateEssayScoringData): EssayScoringSaveResult =>
  parseApiResponse(EssayScoringSaveResultSchema, asWire(raw), ENTITY)

export const adaptEssayReportComment = (raw: CreateReportCommentData): EssayReportComment =>
  parseApiResponse(EssayReportCommentSchema, asWire(raw), ENTITY)

export const adaptCreatedEssayReport = (raw: CreateReportData): CreatedEssayReport =>
  parseApiResponse(CreatedEssayReportSchema, asWire(raw), ENTITY)

export const adaptEssayReportFile = (raw: GetGroupReportData): EssayReportFile =>
  parseApiResponse(EssayReportFileSchema, raw, ENTITY)
